import { mapState, mapGetters } from 'vuex';
import { parseServerErrors, isServerError, isApiError } from '@/utils/errors';
import ErrorList from '@/components/ErrorList';

export default {
  data() {
    return {
      isLoading: false,
      serverErrors: [],

      password: '',
      username: '',
    };
  },

  methods: {
    async handleFormLogin({ redirect = true, silent = false } = {}) {
      const options = {
        data: {
          [this.loginKey]: this.email,
          password: this.password,
        },
        // HACK: Prevent 401 errors from bubbling and triggering Sentry.
        error(error) {
          if (!isApiError(error) || isServerError(error)) {
            throw error;
          }
        },
      };

      if (redirect) {
        options.redirect = {
          name: this.$route.query.next || 'settings',
        };
      } else {
        options.redirect = false;
      }

      const response = await this.handleFormRequest(() =>
        this.$auth.login(options)
      );

      if (response?.status >= 400) {
        return response;
      }

      const userId = response.data?.data?.id;
      if (userId) {
        this.$store.dispatch('setCurrentUserId', userId);
      }

      this.$store.dispatch('clearAuthenticationEmail');

      if (!silent) {
        this.$toasted.show('Logged in!', {
          type: 'success',
        });
      }

      return response;
    },

    async handleFormSignup({ redirect = true, data = {} } = {}) {
      const options = {
        data: {
          // TODO: Handle camelCase to snake_case automatically.
          email: this.email,
          password: this.password,
          confirm_success_url: this.confirmSuccessUrl,
          ...data,
        },
        // HACK: Prevent 401 errors from bubbling and triggering Sentry.
        error(error) {
          if (!isApiError(error) || isServerError(error)) {
            throw error;
          }
        },
      };

      // NOTE: This redirect is for vue-auth, which tells the vue-router where
      // to redirect the user on success.
      if (redirect) {
        options.redirect = {
          name: this.$route.query.next || 'login',
          query: {
            email: this.email,
          },
        };
      } else {
        options.redirect = false;
      }

      return this.handleFormRequest(() => this.$auth.register(options));
    },

    async handleFormForgotPassword({ silent = false } = {}) {
      await this.handleFormRequest(() =>
        this.axios.post('auth/password', {
          email: this.email,
          redirect_url: this.resetPasswordUrl,
        })
      );

      if (!silent) {
        // TODO: Always showing this toast, even when user is not found.
        // Consider modifying the API to further obscure when user is not found,
        // instead of sending back a descriptive 404 message.
        this.$toasted.show(
          `Please check ${this.email} to reset your password.`,
          {
            type: 'success',
          }
        );
        this.email = '';
      }

      return;
    },

    async handleFormRequest(callback) {
      // TODO: Consider renaming `serverErrors` to something more generic like
      // `userFacingErrors` since these are no longer strictly server errors.
      this.serverErrors = [];
      this.isLoading = true;

      let response;

      try {
        response = await callback();
      } catch (error) {
        this.serverErrors = parseServerErrors(error);
        return error.response;
      } finally {
        this.isLoading = false;
      }

      return response;
    },
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapState(['authenticationEmail']),

    loginKey() {
      return this.email.indexOf('@') === -1 ? 'username' : 'email';
    },

    // NOTE: This url is for devise-token-auth, so it knows where to redirect
    // the user after confirming their account.
    confirmSuccessUrl() {
      const path = this.$router.resolve({ name: 'login' }).href;
      return `${window.location.origin}${path}?email=${encodeURIComponent(
        this.email
      )}`;
    },

    resetPasswordUrl() {
      const path = this.$router.resolve({ name: 'resetPassword' }).href;
      return `${window.location.origin}${path}`;
    },

    email: {
      get() {
        return this.authenticationEmail;
      },

      set(value) {
        this.$store.dispatch('updateAuthenticationEmail', value);
      },
    },
  },

  components: {
    ErrorList,
  },
};
